import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    useDefaultLayout,
    usePanelRef,
    type Layout,
    type LayoutChangedMeta,
} from "react-resizable-panels";
import { useChartOfAccountsQueryForWorkspace } from "@api/accountingReports.api";
import { ImportedAccountMappingCounts } from "@common/helpers/importedAccountMappingResolution";
import { WorkspaceChartEditorContent } from "@components/settings/ChartOfAccountsTemplates/editor/workspaceHost/WorkspaceChartEditorContent";
import { useWorkspaceChartEditorExecutor } from "@components/settings/ChartOfAccountsTemplates/editor/workspaceHost/useWorkspaceChartEditorExecutor";
import { Button } from "@design-system/Button/Button";
import { Drawer } from "@design-system/Drawer";
import { Loader } from "@design-system/Loader/Loader";
import {
    ResizableHandle,
    ResizablePanel,
    ResizablePanelGroup,
} from "@design-system/ResizablePanels/ResizablePanels";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { LeftNavClosedIcon, LeftNavOpenedIcon } from "@icons";
import { CoaMappingCounts } from "./CoaMappingCounts";
import { CoaMappingSourceChartPane } from "./CoaMappingSourceChartPane";
import { CoaMappingRow } from "./coaMappingRowTypes";
import { CoaMappingViewConfig } from "./coaMappingViewConfig";
import styles from "./CoaMappingDrawer.module.scss";

const SOURCE_PANE_ID = "coa-mapping-source-pane";

function isHiddenSourcePaneLayout(layout: Layout): boolean {
    const sourcePaneSize = layout[SOURCE_PANE_ID];
    return sourcePaneSize !== undefined && sourcePaneSize <= 0;
}

function expandedSourcePaneSizeAfterCommit(
    previous: string | null,
    layout: Layout,
): string | null {
    if (isHiddenSourcePaneLayout(layout)) {
        return previous;
    }
    const sourcePaneSize = layout[SOURCE_PANE_ID];
    if (sourcePaneSize === undefined || sourcePaneSize <= 0) {
        return previous;
    }
    return `${sourcePaneSize}%`;
}

interface Props {
    isOpen: boolean;
    workspaceId: string;
    entityId: number;
    sourceRows: CoaMappingRow[] | null;
    sourceViewConfig: CoaMappingViewConfig;
    importedCount: number;
    counts: ImportedAccountMappingCounts;
    onClose: () => void;
    onChartEdited: () => void;
    // The executor's post-close refresh; a reopen waits on its promise.
    refreshAfterChartEdits: () => Promise<void>;
}

// The mapping host of the nested editor: one business, no page filter to
// share, every gesture lands live. Stays mounted while closed so the executor
// outlives the content, as on the chart page.
export const CoaMappingChartEditorDrawer = ({
    isOpen,
    workspaceId,
    entityId,
    sourceRows,
    sourceViewConfig,
    importedCount,
    counts,
    onClose,
    onChartEdited,
    refreshAfterChartEdits,
}: Props) => {
    const { data, refetch } = useChartOfAccountsQueryForWorkspace(workspaceId);
    const { defaultLayout, onLayoutChanged } = useDefaultLayout({
        groupId: "coa-mapping-editor-split",
        storage: localStorage,
    });
    const sourcePaneRef = usePanelRef();
    const sourcePaneElementRef = useRef<HTMLDivElement>(null);
    const expandedSourcePaneSizeRef = useRef<string | null>(null);
    const [isSourcePaneCollapsed, setIsSourcePaneCollapsed] = useState(false);
    const displayedEntityIds = useMemo(() => [entityId], [entityId]);
    const restoredLayout = useMemo(
        () =>
            defaultLayout && !isHiddenSourcePaneLayout(defaultLayout)
                ? defaultLayout
                : undefined,
        [defaultLayout],
    );

    const handleLayoutChanged = useCallback(
        (layout: Layout, meta: LayoutChangedMeta) => {
            expandedSourcePaneSizeRef.current =
                expandedSourcePaneSizeAfterCommit(
                    expandedSourcePaneSizeRef.current,
                    layout,
                );
            if (isHiddenSourcePaneLayout(layout)) {
                return;
            }
            onLayoutChanged(layout, meta);
        },
        [onLayoutChanged],
    );

    const syncSourcePaneCollapsed = useCallback(() => {
        setIsSourcePaneCollapsed(sourcePaneRef.current?.isCollapsed() ?? false);
    }, [sourcePaneRef]);

    const toggleSourcePane = useCallback(() => {
        const sourcePane = sourcePaneRef.current;
        if (!sourcePane) {
            return;
        }
        if (sourcePane.isCollapsed()) {
            // expand() only remembers collapse(). Drag and Enter hide without
            // that, so put back the last expanded size ourselves.
            const expandedSize = expandedSourcePaneSizeRef.current;
            if (expandedSize) {
                sourcePane.resize(expandedSize);
                return;
            }
            sourcePane.expand();
            return;
        }
        sourcePane.collapse();
    }, [sourcePaneRef]);

    useLayoutEffect(() => {
        const sourcePaneElement = sourcePaneElementRef.current;
        if (!sourcePaneElement) {
            return;
        }
        sourcePaneElement.inert = isSourcePaneCollapsed;
    }, [isSourcePaneCollapsed]);

    useEffect(() => {
        if (!isOpen) {
            setIsSourcePaneCollapsed(false);
        }
    }, [isOpen]);

    const refetchChart = useCallback(async () => {
        const result = await refetch();
        if (result.isError || result.data === undefined) {
            throw result.error ?? new Error("Chart of accounts refetch failed");
        }
    }, [refetch]);

    const executor = useWorkspaceChartEditorExecutor({
        isOpen,
        workspaceId,
        refetchChart,
        invalidateChart: refreshAfterChartEdits,
    });

    // A write that ends without recovery pending landed; a failed one ends
    // with recovery pending in the same render.
    const wasWritingRef = useRef(false);
    useEffect(() => {
        if (
            wasWritingRef.current &&
            !executor.isWriting &&
            !executor.isRecoveryPending
        ) {
            onChartEdited();
        }
        wasWritingRef.current = executor.isWriting;
    }, [executor.isWriting, executor.isRecoveryPending, onChartEdited]);

    const retryAction = executor.isRecoveryPending ? (
        <Button variant="primary" size="sm" onClick={executor.retryRecovery}>
            Retry
        </Button>
    ) : undefined;

    const returnAction = useMemo(
        () => ({ label: "Return to mapping", onClick: onClose }),
        [onClose],
    );

    const sourcePaneToggleLabel = isSourcePaneCollapsed
        ? "Show source chart"
        : "Hide source chart";
    const sourcePaneToggle = (
        <Tooltip content={sourcePaneToggleLabel}>
            <Button
                variant="secondary"
                size="md"
                iconOnly
                className={styles.sourcePaneToggle}
                onClick={toggleSourcePane}
                aria-label={sourcePaneToggleLabel}
                aria-expanded={!isSourcePaneCollapsed}
            >
                {isSourcePaneCollapsed ? (
                    <LeftNavClosedIcon className="icon-color-gray-900" />
                ) : (
                    <LeftNavOpenedIcon className="icon-color-gray-900" />
                )}
            </Button>
        </Tooltip>
    );

    const renderEditor = () => {
        if (
            !data ||
            executor.isAwaitingPreviousSession ||
            executor.isResyncing
        ) {
            return <Loader />;
        }
        return (
            <WorkspaceChartEditorContent
                key={executor.contentKey}
                data={data}
                displayedEntityIds={displayedEntityIds}
                entityFilter={null}
                toolbarPrependAddon={sourcePaneToggle}
                enqueue={executor.enqueue}
                isEditingDisabled={executor.isRecoveryPending}
                disabledActionAddon={retryAction}
                isWriting={executor.isWriting}
                whenIdle={executor.whenIdle}
                getSessionToken={executor.getSessionToken}
                isSessionCurrent={executor.isSessionCurrent}
                isSessionBlocked={executor.isSessionBlocked}
                trackExternalWrite={executor.trackExternalWrite}
                resync={executor.resync}
                islandAddon={
                    <CoaMappingCounts
                        importedCount={importedCount}
                        counts={counts}
                    />
                }
                returnAction={returnAction}
            />
        );
    };

    const renderBody = () => {
        if (!isOpen) {
            return null;
        }
        return (
            <ResizablePanelGroup
                orientation="horizontal"
                defaultLayout={restoredLayout}
                onLayoutChanged={handleLayoutChanged}
            >
                <ResizablePanel
                    id={SOURCE_PANE_ID}
                    defaultSize="25%"
                    minSize={280}
                    maxSize="50%"
                    collapsible
                    collapsedSize="0%"
                    panelRef={sourcePaneRef}
                    onResize={syncSourcePaneCollapsed}
                >
                    <div className={styles.pane} ref={sourcePaneElementRef}>
                        <CoaMappingSourceChartPane
                            rows={sourceRows}
                            viewConfig={sourceViewConfig}
                        />
                    </div>
                </ResizablePanel>
                <ResizableHandle />
                <ResizablePanel id="coa-mapping-editor-pane" minSize="50%">
                    <div className={styles.pane}>{renderEditor()}</div>
                </ResizablePanel>
            </ResizablePanelGroup>
        );
    };

    return (
        <Drawer
            open={isOpen}
            onClose={onClose}
            direction="right"
            size="fullScreen"
            noPadding
            headerAddonLeft={<span>Edit chart of accounts</span>}
        >
            {renderBody()}
        </Drawer>
    );
};
